import React,{useEffect,useState} from "react";
import {toast} from "react-toastify";
import axiosInstance from "../axiosInstance";
import "./UsersSecurity.css";

export default function PasswordManagement(){
 const [accounts,setAccounts]=useState([]);
 const [history,setHistory]=useState([]);
 const [summary,setSummary]=useState({});
 const [search,setSearch]=useState("");
 const [type,setType]=useState("");
 const [busy,setBusy]=useState(null);
 const [loading,setLoading]=useState(false);
 const [issued,setIssued]=useState(null);

 const load=async()=>{
  setLoading(true);
  try{
   const r=await axiosInstance.get("settings/security/password-accounts/",{params:{search,access_type:type}});
   setAccounts(r.data.accounts||[]);
   setHistory(r.data.recent_resets||[]);
   setSummary(r.data.summary||{});
  }finally{setLoading(false)}
 };

 useEffect(()=>{load().catch(()=>toast.error("Could not load accounts."))},[]);

 const reset=async account=>{
  if(!window.confirm(`Issue a temporary password for ${account.full_name}?`))return;
  const key=account.id
   ?`user-${account.id}`
   :account.access_type==="POLICY_HOLDER"
   ?`policy-${account.policy_id}`
   :`agent-${account.salesperson_id}`;
  setBusy(key);
  try{
   const url=account.id
    ?`settings/security/password-accounts/${account.id}/reset/`
    :account.access_type==="POLICY_HOLDER"
    ?`settings/security/password-policyholders/${account.policy_id}/create-login/`
    :`settings/security/password-agents/${account.salesperson_id}/create-login/`;
   const r=await axiosInstance.post(url,{});
   setIssued(r.data);
   toast.success(account.id?"Temporary password issued.":"Agent login created.");
   await load();
  }catch(e){
   toast.error(e?.response?.data?.detail||"The account action failed.");
  }finally{setBusy(null)}
 };

 const copy=async value=>{
  await navigator.clipboard.writeText(value);
  toast.success("Credentials copied.");
 };

 return <section className="security-card password-admin">
  <div className="security-toolbar">
   <div>
    <h3>Password management</h3>
    <p>Policyholders, agents and system users only. Dependants do not have login accounts.</p>
   </div>
  </div>

  <div className="password-account-summary">
   <article><span>Policyholders</span><strong>{summary.policyholders||0}</strong></article>
   <article><span>Agent logins</span><strong>{summary.agents||0}</strong></article>
   <article><span>Registered agents</span><strong>{summary.registered_salespeople||0}</strong></article>
   <article><span>System users</span><strong>{summary.system_users||0}</strong></article>
  </div>

  <form className="password-search-bar" onSubmit={e=>{e.preventDefault();load().catch(()=>toast.error("Search failed."))}}>
   <select value={type} onChange={e=>setType(e.target.value)}>
    <option value="">ALL ACCOUNTS</option>
    <option value="SYSTEM_USER">STAFF & ADMINISTRATORS</option>
    <option value="AGENT">SALES AGENTS & BROKERS</option>
    <option value="POLICY_HOLDER">POLICYHOLDERS</option>
   </select>
   <label className="security-search">
    <i className="bi bi-search"/>
    <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search agent number, name, email, policy number or phone"/>
   </label>
   <button className="security-primary" disabled={loading}>{loading?"Searching...":"Search"}</button>
  </form>

  <div className="security-table-wrap">
   <table>
    <thead><tr><th>Account holder</th><th>Type</th><th>Agent / policy number</th><th>Username</th><th>Contact / branch</th><th>Status</th><th>Action</th></tr></thead>
    <tbody>
     {accounts.map((a,index)=>{
      const key=a.id
       ?`user-${a.id}`
       :a.access_type==="POLICY_HOLDER"
       ?`policy-${a.policy_id||index}`
       :`agent-${a.salesperson_id||index}`;
      const canCreate=!a.id&&["AGENT","POLICY_HOLDER"].includes(a.access_type);
      return <tr key={key}>
       <td><strong>{a.full_name||"UNNAMED ACCOUNT"}</strong><span>{a.role||a.user_type}</span></td>
       <td>{a.access_type}</td>
       <td>{a.access_type==="POLICY_HOLDER"?a.policy_number:a.access_type==="AGENT"?a.agent_code:"—"}</td>
       <td><strong>{a.login_identifier||"NOT SET"}</strong><span>{a.username_label||"Username"}</span></td>
       <td><strong>{a.phone||"NO PHONE"}</strong><span>{a.branch||"NO BRANCH"}</span></td>
       <td><span className={`security-status ${a.is_active?"active":"inactive"}`}>{a.must_change_password?"PASSWORD CHANGE REQUIRED":a.status}</span></td>
       <td>
        <button
         className="security-reset-btn"
         disabled={busy===key}
         onClick={()=>reset(a)}
        >
         <i className={`bi ${canCreate?"bi-person-plus":"bi-key"}`}/>
         {busy===key?"Working...":canCreate?"Create login":"Reset password"}
        </button>
       </td>
      </tr>
     })}
     {!loading&&!accounts.length&&<tr><td colSpan="7"><div className="password-empty">No account or registered sales agent matched the search.</div></td></tr>}
    </tbody>
   </table>
  </div>

  {issued&&<div className="temporary-password-backdrop" role="dialog" aria-modal="true" aria-label="Temporary login credentials">
   <div className="temporary-password-panel temporary-password-panel--modal">
    <header><div><span>TEMPORARY CREDENTIALS — SHOWN ONCE</span><h4>{issued.user.full_name}</h4></div><button type="button" aria-label="Close credentials" onClick={()=>setIssued(null)}>×</button></header>
    <div className="temporary-credentials-grid"><label><span>USERNAME</span><strong>{issued.identifier}</strong><small>{issued.user.username_label||"Login username"}</small></label><label><span>TEMPORARY PASSWORD</span><strong>{issued.temporary_password}</strong><small>Use this password once, then create a private password.</small></label></div>
    <footer><button type="button" onClick={()=>copy(`${issued.identifier}\n${issued.temporary_password}`)}><i className="bi bi-copy"/>Copy credentials</button></footer>
    <p><strong>Send both values exactly as shown.</strong> Policyholders use the policy number, agents use the agent code, and staff use their email address.</p>
   </div>
  </div>}

  <div className="password-history"><h4>Recent password activity</h4>{history.map(x=><article key={x.id}><div><strong>{x.user}</strong><span>{x.identifier||"—"}</span></div><div><b>{x.request_type}</b><span>{new Date(x.created_at).toLocaleString()}</span></div><em>{x.status}</em></article>)}</div>
 </section>
}